import { useEffect, useMemo, useState, type ReactElement } from 'react'
import { compact, startCase } from 'lodash'
import moment from 'moment'
import { AppointmentConfig } from 'app/components/scheduling/AppointmentConfig'
import { AppointmentConfirmation } from 'app/components/scheduling/AppointmentConfirmation'
import { AppointmentTypeSelector } from 'app/components/scheduling/AppointmentType'
import SchedulingWizard from 'app/components/scheduling/SchedulingWizard'
import { BookingReporter, type BookingDetails } from 'app/components/scheduling/inapp/BookingReporter'
import { InAppPrefillController } from 'app/components/scheduling/inapp/InAppPrefillController'
import { InAppPreferredProviderNotice } from 'app/components/scheduling/inapp/InAppPreferredProviderNotice'
import { InAppReviewStep } from 'app/components/scheduling/inapp/InAppReviewStep'
import { Badge } from 'app/components/Badge'
import { PatientStatusBadge } from 'app/components/contacts/PatientStatusBadge'
import { usePreferredProviders } from 'app/components/contacts/usePreferredProviders'
import { useProviderNames } from 'app/components/contacts/useProviderNames'
import { DialogTitle } from 'app/components/ui/dialog'
import { useImmediateRequest } from 'app/hooks/useImmediateRequest'
import { formatDate, getAge } from 'app/date.utils'
import { gqlFetchList, gqlFetchOne } from 'app/lib/graphqlClient'
import { logError } from 'lib/logging'
import type { AppointmentJson, AppointmentTypeType, ContactJson, LocationJson, PatientJson } from 'app/types/entities'
import type { SchedulingDataApiResponse } from 'lib/scheduling'

const PATIENT_FIELDS = `id firstName lastName birthDate insuranceProvider cellPhone email status lastVisit`

// Covers a patient's future bookings so the newest past-dated one is still in
// the page when we filter below.
const LAST_APPT_LOOKAHEAD_LIMIT = 25

function patientDisplayName(patient: PatientJson): string {
  const name = startCase(compact([patient.firstName, patient.lastName]).join(` `))
  return patient.birthDate ? `${name}, ${getAge(patient.birthDate)}` : name
}

// Newest appointment on the record that isn't a future booking. Compared in
// UTC because appointment dates are stored as date-only UTC midnights; a local
// comparison would pull tomorrow's booking into "today" for US timezones.
async function fetchLastApptDate(patientId: string): Promise<string | undefined> {
  try {
    const { hits } = await gqlFetchList<AppointmentJson>(
      `appointments`,
      {
        search: `patientId:${patientId}`,
        sort: { field: `date`, direction: `desc` },
        pagination: { limit: LAST_APPT_LOOKAHEAD_LIMIT },
      },
      `id date`,
    )
    const today = moment.utc()
    return hits.find((a) => a.date && moment.utc(a.date).isSameOrBefore(today, `day`))?.date as string | undefined
  } catch (e) {
    logError(`ScheduleAppointmentContent: failed to fetch last appointment`, e)
    return undefined
  }
}

// Later of the synced appointment history and the PMS's own lastVisit field:
// app-booked appointments can postdate a stale PMS lastVisit, and PMS history
// isn't always fully synced as appointment rows.
function lastApptDate(patient: PatientJson, fetched: Record<string, string | undefined>): string | undefined {
  const fromAppts = patient.id ? fetched[patient.id] : undefined
  const fromPms = patient.lastVisit ? `${patient.lastVisit}` : undefined
  if (fromAppts && fromPms) return moment.utc(fromAppts).isAfter(moment.utc(fromPms)) ? fromAppts : fromPms
  return fromAppts ?? fromPms
}

// Disambiguates same-name patient records (e.g. parent and child, or a stale
// duplicate) so staff can tell which one the contact's history lives on.
function LastApptBadge({ date }: { date?: string }): ReactElement {
  const label = date ? `Last appt ${formatDate({ value: date, dateOnly: true })}` : `No appt history`
  return <Badge label={label} className="ml-auto shrink-0 whitespace-nowrap" />
}

function useScheduleAppointmentContent({
  contact,
  locationId,
  kollaConsumerId,
  active,
}: {
  contact: ContactJson
  locationId: string
  kollaConsumerId?: string
  active: boolean
}) {
  // undefined = initial load, string = user navigated months
  const [day, setDay] = useState<string>()
  const [location, setLocation] = useState<LocationJson>()
  const [locationError, setLocationError] = useState(false)
  const [patients, setPatients] = useState<PatientJson[]>()
  const [lastApptDates, setLastApptDates] = useState<Record<string, string | undefined>>({})

  const patientIds = useMemo(() => contact.patientIds ?? [], [contact.patientIds])
  const patientIdsKey = patientIds.join(`,`)
  // One linked patient → auto-select it. Multiple → force a "who is this for?"
  // pick first (selectedPatientId stays undefined until the user chooses).
  const [selectedPatientId, setSelectedPatientId] = useState<string | undefined>(
    patientIds.length === 1 ? patientIds[0] : undefined,
  )

  const patient = patients?.find((p) => p.id === selectedPatientId)
  const { preferredProvider, preferredHygienist } = usePreferredProviders(selectedPatientId)
  const { names: providerNames } = useProviderNames(kollaConsumerId)

  const needsPatientPick = patientIds.length > 1 && !selectedPatientId

  useEffect(() => {
    if (!active || !patientIds.length) return
    let cancelled = false
    Promise.all(
      patientIds.map(async (id) => {
        const [patient, lastAppt] = await Promise.all([
          gqlFetchOne<PatientJson>('patient', id, PATIENT_FIELDS).catch((e) => {
            logError(`ScheduleAppointmentContent: failed to fetch patient`, e)
            return undefined
          }),
          fetchLastApptDate(id),
        ])
        return { id, patient, lastAppt }
      }),
    ).then((results) => {
      if (cancelled) return
      setPatients(results.map((r) => r.patient).filter((p): p is PatientJson => !!p))
      setLastApptDates(Object.fromEntries(results.map((r) => [r.id, r.lastAppt])))
    })
    return () => {
      cancelled = true
    }
  }, [active, patientIdsKey, patientIds])

  useEffect(() => {
    if (!active || !locationId) return
    let cancelled = false
    setLocationError(false)
    fetch(`/api/scheduling/locations/${locationId}/info`)
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return
        // /info returns the location object directly (e.g. { id, name, timezone,
        // isSchedulerLive }); tolerate a { location } wrapper too, just in case.
        const loc = d?.location ?? d
        if (loc?.id) {
          setLocation(loc)
        } else {
          setLocationError(true)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLocation(undefined)
          setLocationError(true)
        }
      })
    return () => {
      cancelled = true
    }
  }, [active, locationId])

  useEffect(() => {
    if (!active) {
      setDay(undefined)
      setLocation(undefined)
      setLocationError(false)
      setPatients(undefined)
      setLastApptDates({})
      setSelectedPatientId(patientIds.length === 1 ? patientIds[0] : undefined)
    }
  }, [active, patientIdsKey, patientIds])

  const [apptTypesResponse, apptTypesError] = useImmediateRequest<{ appointmentTypes: AppointmentTypeType[] }>(
    active && locationId ? `/api/scheduling/locations/${locationId}/appointment-types` : ``,
    false,
  )
  const dataUrl = day
    ? `/api/scheduling/locations/${locationId}/scheduling-data?day=${day}`
    : `/api/scheduling/locations/${locationId}/scheduling-data`
  const [dataResponse, dataError] = useImmediateRequest<SchedulingDataApiResponse>(
    active && locationId ? dataUrl : ``,
    false,
  )

  // Only offer types enabled for the online scheduler. The appointment-types
  // endpoint currently returns every stored type regardless of the "Scheduler"
  // product toggle (activeScheduler), so it leaks disabled/agent-only types
  // (e.g. "Test", "WebSched New Patient Default") into the picker. Guard here
  // until the backend applies the activeScheduler:true filter itself.
  const appointmentTypes = useMemo(
    () => apptTypesResponse?.appointmentTypes?.filter((type) => type.activeScheduler),
    [apptTypesResponse],
  )
  const startDate = dataResponse?.day || moment().format(`YYYY-MM-DD`)

  const headerName = patient ? patientDisplayName(patient) : contact.patientName

  return {
    day,
    location,
    locationError,
    patients,
    lastApptDates,
    patientIds,
    patientIdsKey,
    selectedPatientId,
    setSelectedPatientId,
    setDay,
    patient,
    preferredProvider,
    preferredHygienist,
    providerNames,
    needsPatientPick,
    apptTypesResponse,
    apptTypesError,
    dataResponse,
    dataError,
    appointmentTypes,
    startDate,
    headerName,
  }
}

type ScheduleAppointmentContentComponentProps = {
  contact: ContactJson
  locationId: string
  kollaConsumerId?: string
  active: boolean
  showHeader?: boolean
  /**
   * Wrap the heading in Radix's `DialogTitle` (via `asChild`, so the same `<h2>`
   * renders with no markup change) instead of a plain `<h2>`. Radix's
   * `DialogContent` wires `DialogTitle` up as the dialog's accessible name via
   * `aria-labelledby`; a plain heading leaves the dialog with no accessible
   * name and trips Radix's dev-mode "DialogContent requires a DialogTitle"
   * warning. Only safe to set true when this renders inside a Radix `Dialog`
   * (`ScheduleAppointmentDialog`) - `DialogTitle` outside a Dialog context
   * throws, which is why the dock never passes this.
   */
  titleAsDialogTitle?: boolean
  onBooked?: (details: BookingDetails) => void
} & ReturnType<typeof useScheduleAppointmentContent>

function ScheduleAppointmentContentComponent({
  contact,
  showHeader = true,
  titleAsDialogTitle = false,
  onBooked,
  location,
  locationError,
  patients,
  lastApptDates,
  patientIds,
  selectedPatientId,
  setSelectedPatientId,
  setDay,
  patient,
  preferredProvider,
  preferredHygienist,
  providerNames,
  needsPatientPick,
  apptTypesError,
  dataResponse,
  dataError,
  appointmentTypes,
  startDate,
  headerName,
}: ScheduleAppointmentContentComponentProps): ReactElement {
  return (
    <div className="flex min-h-0 grow flex-col gap-y-4">
      {showHeader && (
        <div className="flex flex-col gap-1.5 text-center sm:text-left">
          {titleAsDialogTitle ? (
            <DialogTitle asChild>
              <h2 className="text-lg font-semibold leading-none tracking-tight">Schedule Appointment</h2>
            </DialogTitle>
          ) : (
            <h2 className="text-lg font-semibold leading-none tracking-tight">Schedule Appointment</h2>
          )}
          {!needsPatientPick && headerName && (
            <p className="text-sm text-subtle">
              {headerName}
              {patientIds.length > 1 && selectedPatientId && (
                <button
                  type="button"
                  className="ml-2 text-primary-600 hover:underline"
                  onClick={() => setSelectedPatientId(undefined)}
                >
                  Change
                </button>
              )}
            </p>
          )}
        </div>
      )}

      {needsPatientPick ? (
        <div className="flex min-h-0 flex-col gap-y-2">
          <span className="text-sm font-medium leading-tight text-default">Who is this appointment for?</span>
          {patients ? (
            <div className="flex grow flex-col gap-y-1.5 overflow-y-auto">
              {patients.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="flex items-center gap-x-2 rounded-lg border bg-surface px-4 py-2 text-left text-strong shadow-elevation-1 hover:bg-surface-muted"
                  onClick={() => setSelectedPatientId(p.id)}
                >
                  <span className="text-sm font-medium leading-[17.5px]">{patientDisplayName(p)}</span>
                  <PatientStatusBadge status={p.status ?? undefined} />
                  <LastApptBadge date={lastApptDate(p, lastApptDates)} />
                </button>
              ))}
            </div>
          ) : (
            <div className="p-6 text-sm text-subtle">Loading patients…</div>
          )}
        </div>
      ) : locationError || apptTypesError || dataError ? (
        <div className="p-6 text-sm text-subtle">
          Couldn’t load scheduling for this location. Please try again or reload.
        </div>
      ) : !appointmentTypes || !location ? (
        <div className="p-6 text-sm text-subtle">Loading scheduling…</div>
      ) : appointmentTypes.length === 0 ? (
        <div className="p-6 text-sm text-subtle">Online scheduling isn’t set up for this location.</div>
      ) : (
        <SchedulingWizard
          appointmentTypes={appointmentTypes}
          allProviders={dataResponse?.providers}
          allAvailabilities={dataResponse?.availabilities}
          preProcessedSlots={dataResponse?.slots || []}
          location={location}
          startDate={startDate}
          schedulingConfiguration={
            dataResponse?.schedulingConfiguration || {
              granularity: 30,
              allowDoubleBooking: false,
              leadOverrideEnabled: false,
            }
          }
          isDataLoading={!dataResponse}
          onMonthChange={setDay}
          resetDay={() => setDay(undefined)}
        >
          <InAppPrefillController
            patient={patient}
            preferredProvider={preferredProvider}
            preferredHygienist={preferredHygienist}
          />
          {onBooked && <BookingReporter startDate={startDate} onBooked={onBooked} />}
          <InAppPreferredProviderNotice
            preferredProvider={preferredProvider}
            preferredHygienist={preferredHygienist}
            providerNames={providerNames}
          />
          {/* Scroll region: only the step content scrolls when the viewport is
              short, keeping the wizard buttons below always reachable. */}
          <div className="min-h-0 grow overflow-y-auto">
            <SchedulingWizard.Steps heightClassName="md:h-[620px]">
              <div className="p-6 text-sm text-subtle">Preparing…</div>
              <AppointmentTypeSelector />
              <AppointmentConfig location={location} startDate={startDate} />
              <InAppReviewStep
                location={location}
                startDate={startDate}
                patient={patient}
                patientId={selectedPatientId}
              />
              <AppointmentConfirmation startDate={startDate} />
            </SchedulingWizard.Steps>
          </div>
          <SchedulingWizard.Buttons schedulingConfiguration={dataResponse?.schedulingConfiguration ?? undefined} />
        </SchedulingWizard>
      )}
    </div>
  )
}

function ScheduleAppointmentContent(
  props: Omit<ScheduleAppointmentContentComponentProps, keyof ReturnType<typeof useScheduleAppointmentContent>>,
): ReactElement {
  const hookState = useScheduleAppointmentContent({
    contact: props.contact,
    locationId: props.locationId,
    kollaConsumerId: props.kollaConsumerId,
    active: props.active,
  })
  return <ScheduleAppointmentContentComponent {...props} {...hookState} />
}

export { ScheduleAppointmentContent }
